import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import useVehicleBalances from '../hooks/useVehicleBalances';
import TransactionFilters from '../components/TransactionFilters'; // optional, reuse if needed

const VehicleBalances = () => {
  const { balances, isLoading, error, message, refetch } = useVehicleBalances();
  const [localBalances, setLocalBalances] = useState([]);

  // Show toast message if server sends any
  useEffect(() => {
    if (message) {
      toast.success(message);
    }
  }, [message]);

  // Sync balances from hook to local state
  useEffect(() => {
    if (balances) {
      const cleaned = balances.map(b => ({
        ...b,
        vehicle_id: b.vehicle_id || '',
        balance: b.balance ?? 0,
        last_updated: b.last_updated || b.modified_at || ''
      }));
      setLocalBalances(cleaned);
    }
  }, [balances]);

  // Filters state
  const [filters, setFilters] = useState({
    search: '',
    minBalance: '',
    maxBalance: ''
  });

  // Handle filter changes
  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  // Clear all filters
  const handleClearFilters = () => {
    setFilters({
      search: '',
      minBalance: '',
      maxBalance: ''
    });
  };

  // Filter balances based on search and min/max
  const filteredBalances = localBalances.filter(balance => {
    if (filters.search && !balance.vehicle_id.toLowerCase().includes(filters.search.toLowerCase())) {
      return false;
    }
    if (filters.minBalance && balance.balance < parseFloat(filters.minBalance)) return false;
    if (filters.maxBalance && balance.balance > parseFloat(filters.maxBalance)) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-full mx-auto px-2 sm:px-2 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <h1 className="text-2xl font-semibold tracking-tight text-ink">Vehicle Balances</h1>
            <div className="flex items-center space-x-4">
              <button
                onClick={refetch}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Refresh
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-fullxl mx-auto px-2 sm:px-0 lg:px-0 py-4">
        {/* Optional filters */}
        <TransactionFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
        />

        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900">Vehicle Balances</h2>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vehicle ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Balance</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Wallet ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transaction Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Updated</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredBalances.map((balance, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{balance.vehicle_id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{balance.balance}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{balance.wallet_id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{balance.transaction_type}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{balance.last_updated}</td>
                  </tr>
                ))}

                {filteredBalances.length === 0 && !isLoading && (
                  <tr>
                    <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">
                      No balances found
                    </td>
                  </tr>
                )}

                {isLoading && (
                  <tr>
                    <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">
                      Loading...
                    </td>
                  </tr>
                )}

                {/* {error && (
                  <tr>
                    <td colSpan="5" className="px-6 py-4 text-center text-sm text-red-500">
                      {error}
                    </td>
                  </tr>
                )} */}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
};

export default VehicleBalances;
